import React from "react";
import LegalPage from "./LegalPage";

const UPDATED_EN = "Last updated: August 26, 2026";
const UPDATED_HY = "Վերջին թարմացումը՝ 26 օգոստոսի, 2026թ.";
const UPDATED_FR = "Dernière mise à jour : 26 août 2026";

const sectionsEn = [
  {
    id: "scope",
    heading: "1. Introduction and Scope",
    body: (
      <>
        <p>
          This Privacy Policy explains what personal information Greenwich ("<strong>we</strong>," "<strong>us</strong>")
          collects through greenwich.am and related services (the "<strong>Service</strong>"), why we collect
          it, how we use and share it, and the choices you have. It applies to visitors, registered users, and
          organizations that submit program listings. It should be read together with our{" "}
          <a href="/terms">Terms of Service</a>.
        </p>
      </>
    ),
  },
  {
    id: "collect",
    heading: "2. Information We Collect",
    body: (
      <>
        <h3>a) Information you give us directly</h3>
        <ul>
          <li><strong>Account information:</strong> name, surname, username, email address, password (stored in
            hashed form, we never store or can see your plain-text password), and optionally a phone number.</li>
          <li><strong>Profile and interest information:</strong> date of birth, education level, interests and
            categories you select (used for AI Matching), and a profile photo, if you add one.</li>
          <li><strong>Greenwich Buddy information:</strong> if you take part as a mentor or mentee: gender, mentee/
            mentor category, experience or bio text, and contact details you choose to share (such as a phone
            number, additional email, Telegram, Instagram, or Facebook handle).</li>
          <li><strong>Content you post:</strong> messages you send, group/community posts, comments, reactions, and
            program listings or applications you submit.</li>
          <li><strong>Communications with us:</strong> messages you send through the contact form, feedback popup, or
            by email.</li>
          <li><strong>Payment information:</strong> if you make a donation or purchase a Pro subscription, our
            card-payment provider (Ameriabank) processes your payment details; we receive only confirmation of the
            payment and the amount, not your full card or bank details.</li>
        </ul>
        <h3>b) Information from third-party sign-in</h3>
        <p>
          If you register or log in using Google or Facebook, we receive basic profile information from that
          provider (typically your name, email address, and profile picture) as permitted by your settings with
          that provider.
        </p>
        <h3>c) Information collected automatically</h3>
        <ul>
          <li><strong>Usage data:</strong> pages and programs you view, features you use, search terms, and actions
            you take on the Service.</li>
          <li><strong>Device and log data:</strong> IP address, browser type, device type, operating system,
            language settings, and timestamps.</li>
          <li><strong>Cookies and similar technologies:</strong> small files stored on your device, see Section 5.</li>
        </ul>
      </>
    ),
  },
  {
    id: "use",
    heading: "3. How We Use Information",
    body: (
      <>
        <p>We use the information above to:</p>
        <ul>
          <li>create and maintain your account, and authenticate you when you log in;</li>
          <li>operate core features: display your profile to other users as you configure, deliver messages,
            run community groups, and process program applications;</li>
          <li>power <strong>AI Matching</strong>, by comparing your interests, age, and education level against
            available programs to suggest relevant ones;</li>
          <li>run <strong>Greenwich Buddy</strong> matching between mentors and mentees based on the preferences you
            provide;</li>
          <li>send you service communications (account, security, and Buddy-safety notices) and, if you opt in,
            updates about new programs or features;</li>
          <li>maintain safety and integrity: detect and prevent fraud, abuse, spam, and violations of our{" "}
            <a href="/terms">Terms</a>, including reviewing reports made about Buddy conversations or content;</li>
          <li>understand how the Service is used so we can fix bugs and improve it; and</li>
          <li>comply with legal obligations and enforce our agreements.</li>
        </ul>
      </>
    ),
  },
  {
    id: "sharing",
    heading: "4. How We Share Information",
    body: (
      <>
        <p>We do not sell your personal information. We share it only in these situations:</p>
        <ul>
          <li><strong>With other users, as controlled by the feature.</strong> Your profile, posts, and messages are
            visible to the users you'd expect given how you use the Service (for example, your Buddy contact
            details are shared with a matched mentor/mentee, and community posts are visible to members of that
            community).</li>
          <li><strong>With service providers</strong> who process data on our behalf under contract, including our
            hosting provider, image storage (Cloudinary), analytics (Matomo, a self-hosted/privacy-respecting
            analytics tool), email delivery (Resend), and our card-payment processor (Ameriabank). These providers
            can only use your data to provide their service to us.</li>
          <li><strong>With sign-in providers</strong> (Google, Facebook) to the extent needed to authenticate you, if
            you choose to use them.</li>
          <li><strong>For legal reasons</strong>, if required by law, legal process, or to protect the rights,
            safety, or property of Greenwich, our users, or the public (including responding to a Buddy-safety
            report).</li>
          <li><strong>In a business transfer</strong>, if Greenwich is involved in a merger, acquisition, or asset
            sale, your information may be transferred, subject to this Policy or a policy that provides at least
            equivalent protection.</li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    heading: "5. Cookies and Similar Technologies",
    body: (
      <>
        <p>
          We use cookies and similar technologies for two purposes: (a) <strong>essential</strong> cookies that keep
          you logged in, remember your language and cookie preference, and keep the Service secure (these are
          required and cannot be turned off without breaking core functionality), and (b) <strong>functional /
          analytics</strong> cookies that help us understand usage and improve the Service. When you first visit, our
          cookie banner lets you accept or decline the non-essential ones; you can change your choice at any time
          by clearing your browser's site data for greenwich.am, which resets the prompt.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    heading: "6. Data Retention",
    body: (
      <>
        <p>
          We keep your personal information for as long as your account is active and as needed to provide the
          Service. If you delete your account, we delete or anonymize your personal information within a
          reasonable period, except where we need to keep certain records for legal, safety, tax, or dispute-
          resolution purposes (for example, records related to a reported Buddy-safety concern, or donation
          records required for accounting), and except for content already shared with, and retained by, other
          users (such as a delivered message).
        </p>
      </>
    ),
  },
  {
    id: "rights",
    heading: "7. Your Rights and Choices",
    body: (
      <>
        <p>Depending on where you live, you may have the right to:</p>
        <ul>
          <li><strong>Access</strong> the personal information we hold about you;</li>
          <li><strong>Correct</strong> inaccurate or incomplete information, most profile fields can be edited
            directly in your account settings;</li>
          <li><strong>Delete</strong> your account and associated personal information;</li>
          <li><strong>Object to or restrict</strong> certain processing, such as marketing communications;</li>
          <li><strong>Withdraw consent</strong> at any time where we rely on consent (for example, non-essential
            cookies), without affecting processing done before the withdrawal; and</li>
          <li><strong>Receive a copy</strong> of your data in a portable format.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at greenwicharmenia@gmail.com. We may need to verify your
          identity before acting on a request, and may decline requests that are unfounded, excessive, or that we
          are legally entitled to refuse.
        </p>
      </>
    ),
  },
  {
    id: "children",
    heading: "8. Children's Privacy",
    body: (
      <>
        <p>
          The Service is not directed at children under 13, and we do not knowingly collect personal information
          from anyone under 13. Users between 13 and 18 may only use the Service with a parent or guardian's
          knowledge and consent, as described in our <a href="/terms">Terms</a>. If we learn that we collected
          personal information from a child under 13 without appropriate consent, we will delete it. A parent or
          guardian who believes their child has provided us with personal information without permission can
          contact us at greenwicharmenia@gmail.com to request its deletion.
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    heading: "9. International Data Transfers",
    body: (
      <>
        <p>
          We are based in Armenia, but some of our service providers (such as our hosting and image-storage
          providers) may process data on servers located outside Armenia. Where we transfer personal information
          internationally, we take reasonable steps to ensure it receives a comparable level of protection to
          that described in this Policy, including through contractual safeguards with our providers.
        </p>
      </>
    ),
  },
  {
    id: "security",
    heading: "10. Data Security",
    body: (
      <>
        <p>
          We use reasonable technical and organizational measures to protect your information, including
          password hashing, restricted internal access, and encrypted connections (HTTPS). No method of
          transmission or storage is completely secure, and we cannot guarantee absolute security. If you believe
          your account has been compromised, contact us immediately at greenwicharmenia@gmail.com.
        </p>
      </>
    ),
  },
  {
    id: "thirdparty",
    heading: "11. Third-Party Links and Services",
    body: (
      <>
        <p>
          The Service links to third-party websites: program organizers' pages, our social media accounts, and
          our donation processor. This Policy does not cover those third parties' privacy practices; please
          review their own privacy policies before providing them with information.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    heading: "12. Changes to This Policy",
    body: (
      <>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we will update the
          "Last updated" date and, where appropriate, notify you (for example, by email or an in-app notice).
          Continuing to use the Service after changes take effect means you accept the updated Policy.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    heading: "13. Contact Us",
    body: (
      <>
        <p>
          For questions about this Policy or to exercise your privacy rights, contact us at{" "}
          <a href="mailto:greenwicharmenia@gmail.com">greenwicharmenia@gmail.com</a> or by phone at
          +374 94 577 585.
        </p>
      </>
    ),
  },
];

const sectionsHy = [
  {
    id: "scope",
    heading: "1. Ներածություն և կիրառման շրջանակ",
    body: (
      <>
        <p>
          Սույն Գաղտնիության քաղաքականությունը բացատրում է, թե Greenwich-ը («<strong>մենք</strong>») ինչ անձնական
          տվյալներ է հավաքագրում greenwich.am-ի և հարակից ծառայությունների («<strong>Ծառայություն</strong>»)
          միջոցով, ինչու է հավաքագրում դրանք, ինչպես է օգտագործում ու փոխանցում, և ձեզ հասանելի ընտրանքների
          մասին։ Այն կիրառվում է այցելուների, գրանցված օգտատերերի և ծրագրերի ցանկեր ներկայացնող
          կազմակերպությունների նկատմամբ։ Խորհուրդ ենք տալիս այն կարդալ մեր{" "}
          <a href="/terms">Օգտագործման պայմանների</a> հետ միասին։
        </p>
      </>
    ),
  },
  {
    id: "collect",
    heading: "2. Ինչ տեղեկություններ ենք հավաքագրում",
    body: (
      <>
        <h3>ա) Տեղեկություններ, որոնք դուք ուղղակիորեն տրամադրում եք</h3>
        <ul>
          <li><strong>Հաշվի տեղեկություններ.</strong> անուն, ազգանուն, օգտանուն, էլ. փոստի հասցե, գաղտնաբառ (պահվում
            է հեշավորված ձևով, մենք երբեք չենք տեսնում կամ պահում ձեր հստակ գաղտնաբառը), և ցանկության դեպքում՝
            հեռախոսահամար։</li>
          <li><strong>Պրոֆիլի և հետաքրքրությունների տեղեկություններ.</strong> ծննդյան ամսաթիվ, կրթական մակարդակ,
            ընտրած հետաքրքրություններ և կատեգորիաներ (օգտագործվում է AI Matching-ի համար), և պրոֆիլի
            լուսանկար, եթե ավելացրել եք։</li>
          <li><strong>Greenwich Buddy տեղեկություններ.</strong> եթե մասնակցում եք որպես մենթոր կամ մենթի՝ սեռ,
            մենթի/մենթոր կատեգորիա, փորձառություն կամ նկարագրական տեքստ, և կապի տվյալներ, որոնք ընտրում եք
            կիսվել (օրինակ՝ հեռախոսահամար, լրացուցիչ էլ. փոստ, Telegram, Instagram կամ Facebook)։</li>
          <li><strong>Ձեր հրապարակած բովանդակությունը.</strong> ուղարկած նամակներ, խմբի/համայնքի գրառումներ,
            մեկնաբանություններ, ռեակցիաներ, և ներկայացրած ծրագրերի ցանկեր կամ դիմումներ։</li>
          <li><strong>Մեզ հետ նամակագրություն.</strong> կապի ձևի, հետադարձ կապի պատուհանի կամ էլ. փոստի միջոցով
            ուղարկած հաղորդագրություններ։</li>
          <li><strong>Վճարման տեղեկություններ.</strong> եթե նվիրատվություն եք կատարում կամ Pro բաժանորդագրություն
            գնում, մեր քարտային վճարային համակարգը (Ameriabank) մշակում է ձեր վճարման տվյալները. մենք ստանում ենք
            միայն վճարման հաստատումը և գումարը, ոչ թե ձեր քարտի կամ բանկային ամբողջական տվյալները։</li>
        </ul>
        <h3>բ) Տեղեկություններ երրորդ կողմի մուտքից</h3>
        <p>
          Եթե գրանցվում կամ մուտք եք գործում Google-ի կամ Facebook-ի միջոցով, մենք ստանում ենք հիմնական պրոֆիլային
          տեղեկություններ այդ ծառայությունից (սովորաբար՝ ձեր անունը, էլ. փոստի հասցեն և պրոֆիլի նկարը)՝ ձեր տվյալ
          ծառայության հետ կարգավորումների թույլատրած չափով։
        </p>
        <h3>գ) Ավտոմատ հավաքագրվող տեղեկություններ</h3>
        <ul>
          <li><strong>Օգտագործման տվյալներ.</strong> դիտված էջեր և ծրագրեր, օգտագործված գործառույթներ, որոնման
            բառեր և Ծառայության մեջ ձեր կատարած գործողությունները։</li>
          <li><strong>Սարքի և լոգի տվյալներ.</strong> IP հասցե, դիտարկիչի տեսակ, սարքի տեսակ, օպերացիոն
            համակարգ, լեզվի կարգավորումներ և ժամանակային նշումներ։</li>
          <li><strong>Cookie-ներ և նմանատիպ տեխնոլոգիաներ.</strong> ձեր սարքում պահվող փոքր ֆայլեր. տես 5-րդ
            կետը։</li>
        </ul>
      </>
    ),
  },
  {
    id: "use",
    heading: "3. Ինչպես ենք օգտագործում տեղեկությունները",
    body: (
      <>
        <p>Վերոնշյալ տեղեկություններն օգտագործում ենք՝</p>
        <ul>
          <li>ձեր հաշիվը ստեղծելու և պահպանելու, և մուտք գործելիս ձեզ նույնականացնելու համար.</li>
          <li>հիմնական գործառույթներն իրականացնելու համար՝ ձեր պրոֆիլը ցուցադրելով այլ օգտատերերին՝ ձեր
            կարգավորումների համաձայն, նամակներ հասցնելով, համայնքային խմբեր ապահովելով, և ծրագրերի
            դիմումներ մշակելով.</li>
          <li><strong>AI Matching</strong> գործառույթն ապահովելու համար՝ համեմատելով ձեր հետաքրքրությունները,
            տարիքը և կրթական մակարդակը հասանելի ծրագրերի հետ՝ համապատասխան առաջարկներ անելու համար.</li>
          <li><strong>Greenwich Buddy</strong>-ի մենթոր-մենթի համընկնումն ապահովելու համար՝ ձեր տրամադրած
            նախապատվությունների հիման վրա.</li>
          <li>ձեզ ծառայողական հաղորդագրություններ ուղարկելու համար (հաշվի, անվտանգության և Buddy-անվտանգության
            ծանուցումներ), և, ձեր համաձայնությամբ՝ նոր ծրագրերի կամ գործառույթների մասին թարմացումներ.</li>
          <li>անվտանգությունն ու ամբողջականությունը պահպանելու համար՝ խարդախության, չարաշահման, սպամի և մեր{" "}
            <a href="/terms">Պայմանների</a> խախտումների հայտնաբերում ու կանխարգելում, ներառյալ Buddy
            նամակագրության կամ բովանդակության մասին հայտարարությունների վերանայում.</li>
          <li>հասկանալու համար, թե ինչպես է օգտագործվում Ծառայությունը, որպեսզի կարողանանք շտկել սխալները և
            բարելավել այն. և</li>
          <li>իրավական պարտավորությունները կատարելու և մեր համաձայնագրերն ուժի մեջ պահելու համար։</li>
        </ul>
      </>
    ),
  },
  {
    id: "sharing",
    heading: "4. Ինչպես ենք փոխանցում տեղեկությունները",
    body: (
      <>
        <p>Մենք չենք վաճառում ձեր անձնական տվյալները։ Դրանք փոխանցում ենք միայն հետևյալ դեպքերում.</p>
        <ul>
          <li><strong>Այլ օգտատերերի հետ՝ գործառույթով վերահսկվող կերպով։</strong> Ձեր պրոֆիլը, գրառումները և
            նամակագրությունը տեսանելի են այն օգտատերերի համար, ում դուք կսպասեիք՝ Ծառայությունն օգտագործելու
            ձևից ելնելով (օրինակ՝ ձեր Buddy կապի տվյալները կիսվում են ձեզ համընկնող մենթորի/մենթիի հետ, իսկ
            համայնքի գրառումները տեսանելի են այդ համայնքի անդամներին)։</li>
          <li><strong>Ծառայություններ մատուցողների հետ,</strong> ովքեր պայմանագրով մշակում են տվյալները մեր
            անունից, այդ թվում՝ մեր հոսթինգ մատակարարը, նկարների պահեստավորումը (Cloudinary), վերլուծական
            գործիքը (Matomo՝ սեփական սերվերով տեղադրված, գաղտնիությանը հարգող գործիք), էլ. փոստի առաքումը (Resend),
            և մեր քարտային վճարային համակարգը (Ameriabank)։ Այս մատակարարները ձեր տվյալները կարող են
            օգտագործել միայն մեզ ծառայություն մատուցելու համար։</li>
          <li><strong>Մուտքի ծառայություններ մատուցողների հետ</strong> (Google, Facebook)՝ ձեզ նույնականացնելու
            համար անհրաժեշտ չափով, եթե ընտրում եք դրանք օգտագործել։</li>
          <li><strong>Իրավական պատճառներով</strong>՝ եթե պահանջվում է օրենքով, իրավական գործընթացով, կամ
            Greenwich-ի, մեր օգտատերերի կամ հանրության իրավունքները, անվտանգությունը կամ գույքը պաշտպանելու
            համար (ներառյալ Buddy-անվտանգության հայտարարությանն արձագանքելը)։</li>
          <li><strong>Բիզնես փոխանցման դեպքում</strong>՝ եթե Greenwich-ը մասնակցում է միաձուլման, ձեռքբերման կամ
            ակտիվների վաճառքի, ձեր տեղեկությունները կարող են փոխանցվել՝ ենթակա լինելով սույն Քաղաքականությանը
            կամ առնվազն համարժեք պաշտպանություն ապահովող քաղաքականությանը։</li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    heading: "5. Cookie-ներ և նմանատիպ տեխնոլոգիաներ",
    body: (
      <>
        <p>
          Մենք cookie-ներ և նմանատիպ տեխնոլոգիաներ օգտագործում ենք երկու նպատակով՝ (ա) <strong>անհրաժեշտ</strong> cookie-ներ,
          որոնք ձեզ պահում են մուտք գործած վիճակում, հիշում են ձեր լեզուն և cookie-ների նախապատվությունը, և
          ապահովում Ծառայության անվտանգությունը. դրանք պարտադիր են և չեն կարող անջատվել՝ առանց հիմնական
          գործառույթները խախտելու. և (բ) <strong>ֆունկցիոնալ/վերլուծական</strong> cookie-ներ, որոնք օգնում են մեզ
          հասկանալ օգտագործումը և բարելավել Ծառայությունը։ Առաջին այցելության ժամանակ մեր cookie-բանները թույլ է
          տալիս ընդունել կամ մերժել ոչ-անհրաժեշտները. ձեր ընտրությունը կարող եք փոխել ցանկացած պահի՝ մաքրելով
          greenwich.am-ի համար ձեր դիտարկիչի կայքի տվյալները, ինչը զրոյացնում է հուշումը։
        </p>
      </>
    ),
  },
  {
    id: "retention",
    heading: "6. Տվյալների պահպանման ժամկետ",
    body: (
      <>
        <p>
          Ձեր անձնական տվյալները պահում ենք այնքան ժամանակ, քանի դեռ ձեր հաշիվն ակտիվ է, և որքան անհրաժեշտ է
          Ծառայությունն ապահովելու համար։ Եթե ջնջում եք ձեր հաշիվը, մենք ողջամիտ ժամկետում ջնջում կամ
          անանունացնում ենք ձեր անձնական տվյալները, բացառությամբ այն դեպքերի, երբ պետք է պահպանել որոշակի
          գրառումներ իրավական, անվտանգության, հարկային կամ վեճերի լուծման նպատակով (օրինակ՝ Buddy-անվտանգության
          հայտարարությանն առնչվող գրառումներ, կամ հաշվապահության համար պահանջվող նվիրատվության գրառումներ), և
          բացառությամբ արդեն այլ օգտատերերի հետ կիսված և նրանց մոտ պահվող բովանդակության (օրինակ՝ հասցված
          նամակ)։
        </p>
      </>
    ),
  },
  {
    id: "rights",
    heading: "7. Ձեր իրավունքներն ու ընտրանքները",
    body: (
      <>
        <p>Կախված ձեր բնակության վայրից, դուք կարող եք ունենալ իրավունք՝</p>
        <ul>
          <li><strong>Հասանելիություն</strong>՝ ձեր մասին մեզ մոտ պահվող անձնական տվյալներին.</li>
          <li><strong>Ուղղում</strong>՝ ոչ ճշգրիտ կամ թերի տեղեկությունների. պրոֆիլի դաշտերի մեծ մասը կարող եք
            ուղղակիորեն խմբագրել ձեր հաշվի կարգավորումներում.</li>
          <li><strong>Ջնջում</strong>՝ ձեր հաշվի և կապակցված անձնական տվյալների.</li>
          <li><strong>Առարկություն կամ սահմանափակում</strong>՝ որոշակի մշակման, օրինակ՝ մարքեթինգային
            նամակագրության.</li>
          <li><strong>Համաձայնության հետկանչում</strong>՝ ցանկացած պահի, երբ մենք հենվում ենք համաձայնության վրա
            (օրինակ՝ ոչ-անհրաժեշտ cookie-ներ), առանց դա ազդելու հետկանչից առաջ կատարված մշակման վրա. և</li>
          <li><strong>Ձեր տվյալների պատճենի ստացում</strong>՝ փոխադրելի ձևաչափով։</li>
        </ul>
        <p>
          Այս իրավունքներից որևէ մեկն իրացնելու համար կապվեք մեզ հետ greenwicharmenia@gmail.com հասցեով։ Հայցին
          արձագանքելուց առաջ մենք կարող ենք պահանջել ձեր ինքնության հաստատում, և կարող ենք մերժել
          անհիմն, չափազանցված, կամ օրենքով մերժելու մեր իրավունքի ներքո գտնվող հայցեր։
        </p>
      </>
    ),
  },
  {
    id: "children",
    heading: "8. Երեխաների գաղտնիությունը",
    body: (
      <>
        <p>
          Ծառայությունը ուղղված չէ 13 տարեկանից փոքր երեխաներին, և մենք գիտակցաբար չենք հավաքագրում 13 տարեկանից
          փոքր որևէ մեկի անձնական տվյալները։ 13-ից 18 տարեկան օգտատերերը կարող են օգտագործել Ծառայությունը միայն
          ծնողի կամ խնամակալի իմացությամբ և համաձայնությամբ՝ ինչպես նկարագրված է մեր{" "}
          <a href="/terms">Պայմաններում</a>։ Եթե պարզենք, որ առանց պատշաճ համաձայնության հավաքագրել ենք 13
          տարեկանից փոքր երեխայի անձնական տվյալները, մենք կջնջենք դրանք։ Ծնող կամ խնամակալ, ով կարծում է, որ
          իր երեխան մեզ անձնական տվյալներ է տրամադրել առանց թույլտվության, կարող է կապվել մեզ հետ
          greenwicharmenia@gmail.com հասցեով՝ դրանց ջնջումը պահանջելու համար։
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    heading: "9. Տվյալների միջազգային փոխանցում",
    body: (
      <>
        <p>
          Մենք գործում ենք Հայաստանում, սակայն մեր որոշ ծառայություններ մատուցողներ (օրինակ՝ մեր հոսթինգ և
          նկարների պահեստավորման մատակարարները) կարող են տվյալները մշակել Հայաստանից դուրս գտնվող սերվերների
          վրա։ Երբ անձնական տվյալները միջազգայնորեն ենք փոխանցում, մենք ողջամիտ քայլեր ենք ձեռնարկում ապահովելու
          համար, որ դրանք ստանան սույն Քաղաքականությունում նկարագրվածին համարժեք պաշտպանության մակարդակ,
          այդ թվում՝ մեր մատակարարների հետ պայմանագրային երաշխիքների միջոցով։
        </p>
      </>
    ),
  },
  {
    id: "security",
    heading: "10. Տվյալների անվտանգություն",
    body: (
      <>
        <p>
          Ձեր տեղեկությունները պաշտպանելու համար մենք կիրառում ենք ողջամիտ տեխնիկական և կազմակերպչական
          միջոցներ, ներառյալ գաղտնաբառերի հեշավորումը, ներքին մուտքի սահմանափակումը և գաղտնագրված կապերը
          (HTTPS)։ Փոխանցման կամ պահպանման ոչ մի եղանակ լիովին անվտանգ չէ, և մենք չենք կարող երաշխավորել
          բացարձակ անվտանգություն։ Եթե կարծում եք, որ ձեր հաշիվը վտանգված է, անմիջապես կապվեք մեզ հետ
          greenwicharmenia@gmail.com հասցեով։
        </p>
      </>
    ),
  },
  {
    id: "thirdparty",
    heading: "11. Երրորդ կողմի հղումներ և ծառայություններ",
    body: (
      <>
        <p>
          Ծառայությունը հղումներ է պարունակում երրորդ կողմի կայքերի՝ ծրագրերի կազմակերպիչների էջեր, մեր
          սոցիալական ցանցերի հաշիվներ և մեր նվիրատվության վճարային համակարգ։ Սույն Քաղաքականությունը չի ընդգրկում
          այդ երրորդ կողմերի գաղտնիության պրակտիկան. խնդրում ենք ծանոթանալ նրանց սեփական գաղտնիության
          քաղաքականություններին՝ նախքան նրանց տեղեկություններ տրամադրելը։
        </p>
      </>
    ),
  },
  {
    id: "changes",
    heading: "12. Փոփոխություններ սույն Քաղաքականությունում",
    body: (
      <>
        <p>
          Մենք ժամանակ առ ժամանակ կարող ենք թարմացնել սույն Գաղտնիության քաղաքականությունը։ Էական
          փոփոխությունների դեպքում կթարմացնենք «Վերջին թարմացումը» ամսաթիվը և, անհրաժեշտության դեպքում,
          կտեղեկացնենք ձեզ (օրինակ՝ էլ. փոստով կամ հավելվածի ներսում ծանուցումով)։ Փոփոխություններն ուժի մեջ
          մտնելուց հետո Ծառայության շարունակական օգտագործումը նշանակում է, որ դուք ընդունում եք թարմացված
          Քաղաքականությունը։
        </p>
      </>
    ),
  },
  {
    id: "contact",
    heading: "13. Կապ մեզ հետ",
    body: (
      <>
        <p>
          Սույն Քաղաքականության վերաբերյալ հարցերի կամ ձեր գաղտնիության իրավունքներն իրացնելու համար կապվեք մեզ
          հետ <a href="mailto:greenwicharmenia@gmail.com">greenwicharmenia@gmail.com</a> հասցեով կամ
          +374 94 577 585 հեռախոսահամարով։
        </p>
      </>
    ),
  },
];

// French version, for the France/EU market (fr.greenwich.am). Differs from
// the EN/HY text in three deliberate ways: §2/§8 use 15 as the minimum age
// (France's GDPR Art. 8 digital-consent age) instead of 13; §3 names the
// GDPR Art. 6(1) legal basis for each processing purpose, which the EN/HY
// version (written for a non-GDPR audience) doesn't bother with; and §9
// explicitly addresses the Armenia-to-EU transfer rather than speaking only
// in general terms about "outside Armenia".
const sectionsFr = [
  {
    id: "scope",
    heading: "1. Introduction et champ d'application",
    body: (
      <>
        <p>
          La présente Politique de confidentialité explique quelles informations personnelles Greenwich («{" "}
          <strong>nous</strong> ») collecte via fr.greenwich.am et les services associés (le «{" "}
          <strong>Service</strong> »), pourquoi nous les collectons, comment nous les utilisons et les partageons,
          et les choix dont vous disposez. Elle s'applique aux visiteurs, utilisateurs inscrits, et organisations
          soumettant des annonces de programmes. Elle doit être lue conjointement avec nos{" "}
          <a href="/terms">Conditions d'utilisation</a>.
        </p>
      </>
    ),
  },
  {
    id: "collect",
    heading: "2. Informations que nous collectons",
    body: (
      <>
        <h3>a) Informations que vous nous fournissez directement</h3>
        <ul>
          <li><strong>Informations de compte :</strong> nom, prénom, nom d'utilisateur, adresse e-mail, mot de
            passe (stocké sous forme hachée, nous ne stockons ni ne pouvons jamais voir votre mot de passe en
            clair), et optionnellement un numéro de téléphone.</li>
          <li><strong>Informations de profil et d'intérêts :</strong> date de naissance, niveau d'études, centres
            d'intérêt et catégories que vous sélectionnez (utilisés pour AI Matching), et une photo de profil, si
            vous en ajoutez une.</li>
          <li><strong>Informations Greenwich Buddy :</strong> si vous participez en tant que mentor ou mentoré :
            genre, catégorie de mentorat, texte d'expérience ou de présentation, et coordonnées que vous choisissez
            de partager (téléphone, e-mail supplémentaire, Telegram, Instagram ou Facebook).</li>
          <li><strong>Contenu que vous publiez :</strong> messages envoyés, publications de groupe/communauté,
            commentaires, réactions, et annonces de programmes ou candidatures soumises.</li>
          <li><strong>Communications avec nous :</strong> messages envoyés via le formulaire de contact, le module
            de retour, ou par e-mail.</li>
          <li><strong>Informations de paiement :</strong> si vous faites un don ou achetez un abonnement Pro, notre
            prestataire de paiement par carte (Ameriabank) traite vos données de paiement ; nous ne recevons que
            la confirmation du paiement et le montant, jamais vos coordonnées bancaires complètes.</li>
        </ul>
        <h3>b) Informations issues d'une connexion tierce</h3>
        <p>
          Si vous vous inscrivez ou vous connectez via Google ou Facebook, nous recevons des informations de
          profil de base de ce fournisseur (généralement votre nom, votre adresse e-mail et votre photo de profil),
          dans la mesure autorisée par vos paramètres chez ce fournisseur.
        </p>
        <h3>c) Informations collectées automatiquement</h3>
        <ul>
          <li><strong>Données d'utilisation :</strong> pages et programmes consultés, fonctionnalités utilisées,
            termes de recherche, et actions effectuées sur le Service.</li>
          <li><strong>Données d'appareil et de journalisation :</strong> adresse IP, type de navigateur, type
            d'appareil, système d'exploitation, paramètres de langue, et horodatages.</li>
          <li><strong>Cookies et technologies similaires :</strong> petits fichiers stockés sur votre appareil,
            voir la Section 5. Les cookies/l'analyse non essentiels ne sont utilisés qu'avec votre consentement
            préalable ; une mesure d'audience de base, anonyme et sans cookie persistant, fonctionne sans
            consentement, voir la Section 5.</li>
        </ul>
      </>
    ),
  },
  {
    id: "use",
    heading: "3. Comment nous utilisons les informations",
    body: (
      <>
        <p>Nous utilisons les informations ci-dessus pour (avec, entre parenthèses, la base légale au sens de
          l'article 6(1) du RGPD) :</p>
        <ul>
          <li>créer et maintenir votre compte, et vous authentifier lors de la connexion <em>(exécution du
            contrat)</em> ;</li>
          <li>faire fonctionner les fonctionnalités principales : afficher votre profil aux autres utilisateurs
            selon votre configuration, acheminer les messages, gérer les groupes communautaires, et traiter les
            candidatures aux programmes <em>(exécution du contrat)</em> ;</li>
          <li>faire fonctionner <strong>AI Matching</strong>, en comparant vos centres d'intérêt, votre âge et
            votre niveau d'études aux programmes disponibles pour en suggérer de pertinents <em>(exécution du
            contrat)</em> ;</li>
          <li>faire fonctionner la mise en relation <strong>Greenwich Buddy</strong> entre mentors et mentorés selon
            les préférences que vous indiquez <em>(exécution du contrat)</em> ;</li>
          <li>vous envoyer des communications de service (avis relatifs au compte, à la sécurité, et à la sécurité
            Buddy) et, si vous y consentez, des actualités sur de nouveaux programmes ou fonctionnalités{" "}
            <em>(exécution du contrat ; consentement pour les actualités)</em> ;</li>
          <li>maintenir la sécurité et l'intégrité : détecter et prévenir la fraude, les abus, le spam et les
            violations de nos <a href="/terms">Conditions</a>, y compris l'examen des signalements concernant des
            conversations ou contenus Buddy <em>(intérêt légitime)</em> ;</li>
          <li>comprendre l'utilisation du Service afin de corriger des bugs et de l'améliorer <em>(intérêt
            légitime ; consentement pour l'analyse non essentielle, voir Section 5)</em> ; et</li>
          <li>respecter nos obligations légales et faire valoir nos accords <em>(obligation légale ; intérêt
            légitime)</em>.</li>
        </ul>
      </>
    ),
  },
  {
    id: "sharing",
    heading: "4. Comment nous partageons les informations",
    body: (
      <>
        <p>Nous ne vendons pas vos informations personnelles. Nous ne les partageons que dans les cas suivants :</p>
        <ul>
          <li><strong>Avec d'autres utilisateurs, selon ce que contrôle la fonctionnalité.</strong> Votre profil,
            vos publications et vos messages sont visibles par les utilisateurs auxquels vous vous attendriez
            compte tenu de votre usage du Service (par exemple, vos coordonnées Buddy sont partagées avec un
            mentor/mentoré mis en relation, et les publications communautaires sont visibles par les membres de
            cette communauté).</li>
          <li><strong>Avec des prestataires de services</strong> qui traitent des données pour notre compte dans le
            cadre d'un contrat, notamment notre hébergeur, le stockage d'images (Cloudinary), l'analyse (Matomo,
            un outil auto-hébergé respectueux de la vie privée), l'envoi d'e-mails (Resend), et notre prestataire
            de paiement par carte (Ameriabank). Ces prestataires ne peuvent utiliser vos données que pour nous
            fournir leur service.</li>
          <li><strong>Avec les fournisseurs de connexion</strong> (Google, Facebook) dans la mesure nécessaire pour
            vous authentifier, si vous choisissez de les utiliser.</li>
          <li><strong>Pour des raisons légales</strong>, si la loi, une procédure judiciaire, ou la protection des
            droits, de la sécurité ou des biens de Greenwich, de nos utilisateurs ou du public l'exigent (y compris
            pour répondre à un signalement de sécurité Buddy).</li>
          <li><strong>Lors d'un transfert d'activité</strong>, si Greenwich est impliqué dans une fusion, une
            acquisition ou une cession d'actifs, vos informations pourront être transférées, sous réserve de la
            présente Politique ou d'une politique offrant une protection au moins équivalente.</li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    heading: "5. Cookies et technologies similaires",
    body: (
      <>
        <p>
          Nous utilisons des cookies et technologies similaires pour deux finalités : (a) les cookies{" "}
          <strong>essentiels</strong>, qui vous maintiennent connecté, mémorisent votre langue et votre choix en
          matière de cookies, et assurent la sécurité du Service (ils sont nécessaires et ne peuvent être désactivés
          sans compromettre les fonctionnalités principales) ; et (b) les cookies/technologies{" "}
          <strong>fonctionnels/analytiques</strong>, qui nous aident à comprendre l'utilisation du Service et à
          l'améliorer. Notre outil de mesure d'audience (Matomo) fonctionne par défaut en mode anonyme, sans cookie
          ni identifiant persistant, ce qui ne nécessite pas de consentement ; lors de votre première visite, notre
          bandeau cookies vous permet d'accepter le suivi complet par cookies, qui active alors la reconnaissance
          des visiteurs récurrents. Vous pouvez modifier votre choix à tout moment en effaçant les données de site
          de votre navigateur pour fr.greenwich.am, ce qui réaffiche le bandeau.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    heading: "6. Conservation des données",
    body: (
      <>
        <p>
          Nous conservons vos informations personnelles aussi longtemps que votre compte est actif et que
          nécessaire pour fournir le Service. Si vous supprimez votre compte, nous supprimons ou anonymisons vos
          informations personnelles dans un délai raisonnable, sauf lorsque nous devons conserver certains
          enregistrements à des fins légales, de sécurité, fiscales ou de résolution de litiges (par exemple, des
          enregistrements liés à un signalement de sécurité Buddy, ou des enregistrements de dons requis à des
          fins comptables), et à l'exception du contenu déjà partagé avec, et conservé par, d'autres utilisateurs
          (comme un message déjà remis).
        </p>
      </>
    ),
  },
  {
    id: "rights",
    heading: "7. Vos droits et choix",
    body: (
      <>
        <p>En tant que résident de l'UE, vous disposez des droits suivants en vertu du RGPD (et, où qu'ils
          résident, les autres utilisateurs peuvent disposer de droits similaires selon leur droit local) :</p>
        <ul>
          <li><strong>Accéder</strong> aux informations personnelles que nous détenons à votre sujet ;</li>
          <li><strong>Rectifier</strong> les informations inexactes ou incomplètes, la plupart des champs de
            profil peuvent être modifiés directement dans les paramètres de votre compte ;</li>
          <li><strong>Supprimer</strong> votre compte et les informations personnelles associées ;</li>
          <li><strong>Vous opposer à, ou restreindre,</strong> certains traitements, comme les communications
            marketing ;</li>
          <li><strong>Retirer votre consentement</strong> à tout moment lorsque nous nous appuyons sur le
            consentement (par exemple, les cookies non essentiels), sans affecter le traitement effectué avant ce
            retrait ;</li>
          <li><strong>Recevoir une copie</strong> de vos données dans un format portable ; et</li>
          <li><strong>Introduire une réclamation</strong> auprès de votre autorité de contrôle (en France, la
            CNIL), sans préjudice de tout autre recours.</li>
        </ul>
        <p>
          Pour exercer l'un de ces droits, contactez-nous à greenwicharmenia@gmail.com. Nous pourrons avoir besoin
          de vérifier votre identité avant de donner suite à une demande, et pouvons refuser les demandes
          infondées, excessives, ou que nous sommes légalement autorisés à refuser.
        </p>
      </>
    ),
  },
  {
    id: "children",
    heading: "8. Confidentialité des mineurs",
    body: (
      <>
        <p>
          Le Service ne s'adresse pas aux enfants de moins de 15 ans, et nous ne collectons pas sciemment
          d'informations personnelles auprès de toute personne de moins de 15 ans. Les utilisateurs âgés de 15 à
          18 ans ne peuvent utiliser le Service qu'avec la connaissance et le consentement d'un parent ou tuteur,
          comme décrit dans nos <a href="/terms">Conditions</a>. Si nous apprenons avoir collecté des informations
          personnelles d'un enfant de moins de 15 ans sans le consentement requis, nous les supprimerons. Un parent
          ou tuteur estimant que son enfant nous a fourni des informations personnelles sans autorisation peut nous
          contacter à greenwicharmenia@gmail.com pour en demander la suppression.
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    heading: "9. Transferts internationaux de données",
    body: (
      <>
        <p>
          Nous sommes basés en Arménie, qui ne fait pas l'objet d'une décision d'adéquation de la Commission
          européenne : vos données, lorsque vous résidez dans l'UE, sont donc transférées hors de l'Espace
          économique européen vers l'Arménie, ainsi que, le cas échéant, vers les pays où sont situés nos
          prestataires (hébergement, stockage d'images, paiement). Nous encadrons ces transferts par des garanties
          contractuelles appropriées (notamment des clauses contractuelles types, lorsqu'applicable) avec ces
          prestataires, afin que vos données bénéficient d'un niveau de protection substantiellement équivalent à
          celui décrit dans la présente Politique. Vous pouvez demander une copie des garanties applicables en nous
          contactant à greenwicharmenia@gmail.com.
        </p>
      </>
    ),
  },
  {
    id: "security",
    heading: "10. Sécurité des données",
    body: (
      <>
        <p>
          Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables pour protéger vos
          informations, notamment le hachage des mots de passe, un accès interne restreint, et des connexions
          chiffrées (HTTPS). Aucune méthode de transmission ou de stockage n'est totalement sécurisée, et nous ne
          pouvons garantir une sécurité absolue. Si vous pensez que votre compte a été compromis, contactez-nous
          immédiatement à greenwicharmenia@gmail.com.
        </p>
      </>
    ),
  },
  {
    id: "thirdparty",
    heading: "11. Liens et services tiers",
    body: (
      <>
        <p>
          Le Service comporte des liens vers des sites tiers : pages des organisateurs de programmes, nos comptes
          de réseaux sociaux, et notre prestataire de paiement. La présente Politique ne couvre pas les pratiques
          de confidentialité de ces tiers ; veuillez consulter leurs propres politiques de confidentialité avant de
          leur fournir des informations.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    heading: "12. Modifications de la présente Politique",
    body: (
      <>
        <p>
          Nous pouvons mettre à jour la présente Politique de confidentialité de temps à autre. En cas de
          modification substantielle, nous mettrons à jour la date de « Dernière mise à jour » et, le cas échéant,
          vous en informerons (par exemple par e-mail ou notification dans l'application). Le fait de continuer à
          utiliser le Service après l'entrée en vigueur des modifications signifie que vous acceptez la Politique
          mise à jour.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    heading: "13. Nous contacter",
    body: (
      <>
        <p>
          Pour toute question concernant la présente Politique ou pour exercer vos droits en matière de
          confidentialité, contactez-nous à{" "}
          <a href="mailto:greenwicharmenia@gmail.com">greenwicharmenia@gmail.com</a> ou au +374 94 577 585.
        </p>
      </>
    ),
  },
];

function Privacy() {
  return (
    <LegalPage
      titleEn="Privacy Policy"
      titleHy="Գաղտնիության քաղաքականություն"
      titleFr="Politique de confidentialité"
      updatedEn={UPDATED_EN}
      updatedHy={UPDATED_HY}
      updatedFr={UPDATED_FR}
      sectionsEn={sectionsEn}
      sectionsHy={sectionsHy}
      sectionsFr={sectionsFr}
      seoTitleKey="pages.seo.privacy.title"
      seoDescKey="pages.seo.privacy.description"
      seoKeywordsKey="pages.seo.privacy.keywords"
      seoPath="/privacy"
    />
  );
}

export default Privacy;
